var _excluded = ["defaultSelectedKey", "disabledKeys", "getInitialScrollPosition", "normalizedItems", "onChange", "onOpenChange", "onScroll", "onSelectionChange", "selectedKey", "showItemIcons", "tooltip"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], -1 === t.indexOf(o) && {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (-1 !== e.indexOf(n)) continue; t[n] = r[n]; } return t; }
import { useCallback, useMemo } from 'react';
import { EMPTY_FUNCTION } from '@deephaven/utils';
import { getItemKey, isNormalizedSection, normalizeTooltipOptions, useRenderNormalizedItem, useStringifiedSelection } from "../utils/index.js";
import { usePickerScrollOnOpen } from "./usePickerScrollOnOpen.js";
import { Section } from "../shared.js";
/** Props that are derived by `usePickerNormalizedProps` */
/**
 * Props that are passed through untouched. (should exclude all of the
 * destructured props passed into `usePickerNormalizedProps` that are not in the
 * spread ...props)
 */
/** Props returned from `usePickerNormalizedProps` hook. */
import { jsx as _jsx } from "react/jsx-runtime";
export function usePickerNormalizedProps(_ref) {
  var defaultSelectedKey = _ref.defaultSelectedKey,
    disabledKeys = _ref.disabledKeys,
    getInitialScrollPosition = _ref.getInitialScrollPosition,
    normalizedItems = _ref.normalizedItems,
    onChange = _ref.onChange,
    onOpenChange = _ref.onOpenChange,
    _ref$onScroll = _ref.onScroll,
    onScroll = _ref$onScroll === void 0 ? EMPTY_FUNCTION : _ref$onScroll,
    onSelectionChange = _ref.onSelectionChange,
    selectedKey = _ref.selectedKey,
    showItemIcons = _ref.showItemIcons,
    _ref$tooltip = _ref.tooltip,
    tooltip = _ref$tooltip === void 0 ? true : _ref$tooltip,
    props = _objectWithoutProperties(_ref, _excluded);
  var tooltipOptions = useMemo(() => normalizeTooltipOptions(tooltip), [tooltip]);
  var renderNormalizedItem = useRenderNormalizedItem({
    itemIconSlot: 'icon',
    // Descriptions introduce variable item heights which throws off calculation
    // of initial scroll position and setting viewport on windowed data. For now
    // not going to implement description support in Picker.
    // https://github.com/deephaven/web-client-ui/issues/1958
    showItemDescriptions: false,
    showItemIcons,
    tooltipOptions
  });

  // Spectrum doesn't re-render if only the `renderNormalizedItems` function
  // changes, so we create a key from its dependencies that can be used to force
  // re-render.
  var forceRerenderKey = "".concat(showItemIcons, "-").concat(tooltipOptions === null || tooltipOptions === void 0 ? void 0 : tooltipOptions.placement);
  var _usePickerScrollOnOpe = usePickerScrollOnOpen({
      getInitialScrollPosition,
      onScroll,
      onOpenChange
    }),
    ref = _usePickerScrollOnOpe.ref,
    onOpenChangeInternal = _usePickerScrollOnOpe.onOpenChange;

  // Spectrum Picker treats keys as strings if the `key` prop is explicitly
  // set on `Item` elements. Since we do this in `renderItem`, we need to
  // map original key types to and from strings so that selection works.
  var _useStringifiedSelect = useStringifiedSelection({
      normalizedItems,
      selectedKey,
      defaultSelectedKey,
      disabledKeys,
      onChange: onChange !== null && onChange !== void 0 ? onChange : onSelectionChange
    }),
    selectedStringKey = _useStringifiedSelect.selectedStringKey,
    defaultSelectedStringKey = _useStringifiedSelect.defaultSelectedStringKey,
    disabledStringKeys = _useStringifiedSelect.disabledStringKeys,
    onStringSelectionChange = _useStringifiedSelect.onStringSelectionChange;
  var children = useCallback(itemOrSection => {
    if (isNormalizedSection(itemOrSection)) {
      var _itemOrSection$item, _itemOrSection$item2;
      return /*#__PURE__*/_jsx(Section, {
        title: (_itemOrSection$item = itemOrSection.item) === null || _itemOrSection$item === void 0 ? void 0 : _itemOrSection$item.title,
        items: (_itemOrSection$item2 = itemOrSection.item) === null || _itemOrSection$item2 === void 0 ? void 0 : _itemOrSection$item2.items,
        children: renderNormalizedItem
      }, getItemKey(itemOrSection));
    }
    return renderNormalizedItem(itemOrSection);
  }, [renderNormalizedItem]);
  return _objectSpread(_objectSpread({}, props), {}, {
    children,
    forceRerenderKey,
    ref,
    items: normalizedItems,
    selectedKey: selectedStringKey,
    defaultSelectedKey: defaultSelectedStringKey,
    disabledKeys: disabledStringKeys,
    onSelectionChange: onStringSelectionChange,
    onOpenChange: onOpenChangeInternal
  });
}
export default usePickerNormalizedProps;
//# sourceMappingURL=usePickerNormalizedProps.js.map